import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

const MessageInput = ({
  onSendMessage,
  disabled = false,
  placeholder = "Type your message...",
  maxLength = 5000,
  referencedDocuments = [],
  onRemoveReference,
  // NEW: Props for chat document upload
  sessionId = null,
  chatDocuments = [],
  onUploadChatDocument,
  onRemoveChatDocument,
}) => {
  const [message, setMessage] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState("8rem");
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Calculate max height based on viewport
  useEffect(() => {
    const calculateMaxHeight = () => {
      const viewportHeight = window.innerHeight;
      const maxHeightPx = Math.floor(viewportHeight * 0.35); // 35% of viewport height
      const minHeightPx = 128; // 8rem = 128px minimum
      const finalHeight = Math.max(maxHeightPx, minHeightPx);
      setMaxHeight(`${finalHeight}px`);
    };

    calculateMaxHeight();
    window.addEventListener("resize", calculateMaxHeight);
    return () => window.removeEventListener("resize", calculateMaxHeight);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeightPx = parseInt(maxHeight);

      // Set height to scrollHeight but don't exceed maxHeight
      textareaRef.current.style.height = `${Math.min(
        scrollHeight,
        maxHeightPx
      )}px`;
    }
  }, [message, maxHeight]);

  // Focus on mount
  useEffect(() => {
    if (textareaRef.current && !disabled) {
      textareaRef.current.focus();
    }
  }, [disabled]);

  // Auto-expand when documents are referenced or uploaded
  useEffect(() => {
    if (referencedDocuments.length > 0 || chatDocuments.length > 0) {
      setIsExpanded(true);
    }
  }, [referencedDocuments, chatDocuments]);

  // Listen for predefined prompt selection events
  useEffect(() => {
    const handlePredefinedPromptSelected = (event) => {
      const { prompt } = event.detail;

      // Add the prompt text to the current message
      const newMessage = message.trim()
        ? `${message.trim()}\n\n${prompt.prompt_text}`
        : prompt.prompt_text;

      setMessage(newMessage);
      setIsExpanded(true);

      // Focus textarea after selecting prompt
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Set cursor to end of text
          textareaRef.current.setSelectionRange(
            newMessage.length,
            newMessage.length
          );
        }
      }, 100);
    };

    window.addEventListener(
      "predefinedPromptSelected",
      handlePredefinedPromptSelected
    );

    return () => {
      window.removeEventListener(
        "predefinedPromptSelected",
        handlePredefinedPromptSelected
      );
    };
  }, [message]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      // Send message with both referenced proposal documents and chat documents
      onSendMessage(message.trim(), {
        referencedDocuments: referencedDocuments.map((doc) => doc.document_id),
        chatDocuments: chatDocuments.map((doc) => doc.chat_document_id),
      });
      setMessage("");
      setIsExpanded(referencedDocuments.length > 0 || chatDocuments.length > 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    if (
      !message.trim() &&
      referencedDocuments.length === 0 &&
      chatDocuments.length === 0
    ) {
      setIsExpanded(false);
    }
  };

  // NEW: Handle file upload
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0 || !sessionId) return;

    setUploading(true);

    try {
      for (const file of files) {
        // Validate file size (50MB limit like proposal documents)
        if (file.size > 50 * 1024 * 1024) {
          alert(`File ${file.name} is too large. Maximum size is 50MB.`);
          continue;
        }

        // Validate file type
        const allowedTypes = [
          ".pdf",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".ppt",
          ".pptx",
          ".txt",
          ".csv",
        ];
        const fileExt = "." + file.name.split(".").pop().toLowerCase();

        if (!allowedTypes.includes(fileExt)) {
          alert(`File type ${fileExt} is not supported.`);
          continue;
        }

        await onUploadChatDocument(file, sessionId);
      }
    } catch (error) {
      console.error("File upload failed:", error);
      alert("File upload failed. Please try again.");
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return { icon: "file-pdf", color: "text-red-500" };
      case "doc":
      case "docx":
        return { icon: "file-word", color: "text-blue-500" };
      case "xls":
      case "xlsx":
        return { icon: "file-excel", color: "text-emerald-500" };
      case "ppt":
      case "pptx":
        return { icon: "file-powerpoint", color: "text-orange-500" };
      case "txt":
        return { icon: "file-alt", color: "text-slate-500" };
      case "csv":
        return { icon: "file-csv", color: "text-green-500" };
      default:
        return { icon: "file-alt", color: "text-slate-500" };
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const canSend = message.trim().length > 0 && !disabled;
  const isOverLimit = message.length > maxLength;
  const hasReferences = referencedDocuments.length > 0;
  const hasChatDocuments = chatDocuments.length > 0;
  const hasAnyDocuments = hasReferences || hasChatDocuments;

  return (
    <div className="relative">
      {/* Chat Documents (uploaded to this conversation) */}
      {hasChatDocuments && (
        <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon
                icon="paperclip"
                className="text-purple-600 text-sm"
              />
              <span className="text-sm font-medium text-purple-900">
                Uploaded Documents ({chatDocuments.length})
              </span>
            </div>
            <button
              onClick={() => {
                chatDocuments.forEach((doc) =>
                  onRemoveChatDocument?.(doc.chat_document_id)
                );
              }}
              className="text-xs text-purple-600 hover:text-purple-800 transition-colors"
              title="Clear all uploads"
            >
              Clear all
            </button>
          </div>

          <div className="space-y-2">
            {chatDocuments.map((doc) => {
              const fileIcon = getFileIcon(doc.original_filename);

              return (
                <div
                  key={doc.chat_document_id}
                  className="flex items-center justify-between p-2 bg-white border border-purple-200 rounded-lg"
                >
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <FontAwesomeIcon
                      icon={fileIcon.icon}
                      className={`${fileIcon.color} flex-shrink-0`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {doc.original_filename}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-slate-600">
                        <span>{formatFileSize(doc.file_size)}</span>
                        {doc.processing_status === "pending" && (
                          <>
                            <span>•</span>
                            <span className="text-amber-600 flex items-center">
                              <FontAwesomeIcon
                                icon="spinner"
                                className="animate-spin mr-1"
                              />
                              Processing...
                            </span>
                          </>
                        )}
                        {doc.processing_status === "failed" && (
                          <>
                            <span>•</span>
                            <span className="text-red-600">
                              Failed to process
                            </span>
                          </>
                        )}
                        {doc.processing_status === "completed" && (
                          <>
                            <span>•</span>
                            <span className="text-green-600">Ready</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => onRemoveChatDocument?.(doc.chat_document_id)}
                    className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    title="Remove file"
                  >
                    <FontAwesomeIcon icon="times" className="text-xs" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-2 text-xs text-purple-700">
            💡 These documents are available for this chat conversation.
          </div>
        </div>
      )}

      {/* Referenced Documents (from proposal system) */}
      {hasReferences && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon="link" className="text-blue-600 text-sm" />
              <span className="text-sm font-medium text-blue-900">
                Referenced Documents ({referencedDocuments.length})
              </span>
            </div>
            <button
              onClick={() => {
                // Clear all references
                referencedDocuments.forEach((doc) =>
                  onRemoveReference?.(doc.document_id)
                );
              }}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
              title="Clear all references"
            >
              Clear all
            </button>
          </div>

          <div className="space-y-2">
            {referencedDocuments.map((doc) => {
              const fileIcon = getFileIcon(doc.original_file_name);

              return (
                <div
                  key={doc.document_id}
                  className="flex items-center justify-between p-2 bg-white border border-blue-200 rounded-lg"
                >
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <FontAwesomeIcon
                      icon={fileIcon.icon}
                      className={`${fileIcon.color} flex-shrink-0`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {doc.original_file_name}
                      </p>
                      <p className="text-xs text-slate-600">{doc.type_name}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => onRemoveReference?.(doc.document_id)}
                    className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    title="Remove reference"
                  >
                    <FontAwesomeIcon icon="times" className="text-xs" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-2 text-xs text-blue-700">
            💡 The AI will have access to the content of these documents when
            responding to your question.
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end space-x-3">
        {/* File upload button */}
        {sessionId && (
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading}
              className={clsx(
                "h-12 w-12 rounded-xl flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-4 flex-shrink-0",
                {
                  "bg-slate-100 hover:bg-slate-200 text-slate-600 focus:ring-slate-100":
                    !disabled && !uploading,
                  "bg-slate-50 text-slate-400 cursor-not-allowed":
                    disabled || uploading,
                }
              )}
              title={uploading ? "Uploading..." : "Upload documents"}
            >
              {uploading ? (
                <FontAwesomeIcon icon="spinner" className="animate-spin" />
              ) : (
                <FontAwesomeIcon icon="paperclip" />
              )}
            </button>
          </div>
        )}

        {/* Message textarea */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={
              hasAnyDocuments ? "Ask about the documents..." : placeholder
            }
            disabled={disabled}
            rows={2}
            className={clsx(
              "w-full resize-none rounded-xl border-2 px-4 py-3 pr-12 transition-all duration-200 focus:outline-none overflow-y-auto",
              {
                "border-slate-200 focus:border-primary-300 focus:ring-4 focus:ring-primary-100":
                  !isOverLimit && !hasAnyDocuments,
                "border-purple-300 focus:border-purple-400 focus:ring-4 focus:ring-purple-100":
                  !isOverLimit && hasAnyDocuments,
                "border-red-300 focus:border-red-400 focus:ring-4 focus:ring-red-100":
                  isOverLimit,
                "bg-slate-50 text-slate-500 cursor-not-allowed": disabled,
                "bg-white": !disabled,
              }
            )}
            style={{
              minHeight: "4.5rem",
              maxHeight: isExpanded ? maxHeight : "4.5rem",
              height: "auto",
            }}
          />

          {/* Character count */}
          {(isExpanded || message.length > maxLength * 0.8) && (
            <div
              className={clsx("absolute bottom-2 right-12 text-xs", {
                "text-slate-400": message.length <= maxLength * 0.8,
                "text-amber-500":
                  message.length > maxLength * 0.8 &&
                  message.length <= maxLength,
                "text-red-500": message.length > maxLength,
              })}
            >
              {message.length}/{maxLength}
            </div>
          )}
        </div>

        {/* Send button */}
        <button
          type="submit"
          disabled={!canSend || isOverLimit}
          className={clsx(
            "h-12 w-12 rounded-xl flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-4 flex-shrink-0",
            {
              "bg-primary-500 hover:bg-primary-600 text-white focus:ring-primary-100":
                canSend && !isOverLimit && !hasAnyDocuments,
              "bg-purple-500 hover:bg-purple-600 text-white focus:ring-purple-100":
                canSend && !isOverLimit && hasAnyDocuments,
              "bg-slate-200 text-slate-400 cursor-not-allowed":
                !canSend || isOverLimit || disabled,
            }
          )}
          title={
            !canSend
              ? "Type a message to send"
              : isOverLimit
              ? "Message too long"
              : hasAnyDocuments
              ? "Send message with documents"
              : "Send message"
          }
        >
          {disabled ? (
            <FontAwesomeIcon icon="spinner" className="animate-spin" />
          ) : (
            <FontAwesomeIcon icon="paper-plane" />
          )}
        </button>
      </form>
    </div>
  );
};

export default MessageInput;
